// The canvas's document, and every pure edit to it.
//
// **The state shape is the wire shape.** A `StudioDoc` IS a `StudioDashboard` —
// the exact object `PUT /api/bi/mine/dashboards/{id}` takes and
// `GET` returns, spec and embedded charts together. There is deliberately no
// separate editor model that gets translated on save, because a translation
// layer is where an editor grows a state that can express things the wire
// cannot: a tile referencing a chart that was pruned, a control the spec has no
// filter for, a viz option that quietly does not survive a round trip. Editing
// the wire shape directly means a save is `JSON.stringify` of what is on screen,
// and a reload is what was saved.
//
// **A control is not a grid tile.** The layout IR has exactly two tile kinds
// (chart and markdown), and the canvas does not invent a third: a control is a
// `NativeFilterSpec` in `spec.filters`, rendered by the existing `FilterBar`
// above the grid, which is where a dashboard's controls belong and how the
// existing filter machinery (URL serialization, per-chart scoping, the
// auto-apply rule) reaches them for free. The canvas selects a control the same
// way it selects a tile; it just lives in a different list.
//
// Everything here is a pure function from one document to the next. The canvas
// component holds one `StudioDoc` in state and an undo stack of them.
import type {
  BiSource,
  ChartSpec,
  ChartTile,
  DashboardParameter,
  LayoutTile,
  MarkdownTile,
  NativeFilterSpec,
  SimpleAggregate,
  SimpleMetric,
  StudioDashboard,
} from "@bi/shared/types";

import {
  DEFAULT_TILE_HEIGHT,
  type TileAddress,
  appendTileRow,
  removeTile,
  tileAt,
} from "./canvas-layout";
import { GRID_COLUMNS } from "./dashboard-layout";
import { newUuid } from "./new-uuid";

export type StudioDoc = StudioDashboard;

/** What a person picks from the "add a tile" menu.
 *
 *  Nine kinds over six viz types plus markdown plus a control, because the
 *  useful vocabulary and the IR's vocabulary are not the same list: `pivot` and
 *  `bar` are both `echarts_timeseries_bar` and differ only by whether a
 *  breakdown is set, while `scorecard` is one of two big-number viz types
 *  depending on whether a trend was asked for. `tileKindOf` derives the kind
 *  back out of a chart, so nothing has to be stored twice. */
export type TileKind =
  | "bar"
  | "line"
  | "table"
  | "pivot"
  | "scorecard"
  | "pie"
  | "funnel"
  | "text"
  | "control";

export const TILE_KIND_LABELS: Record<TileKind, string> = {
  bar: "Bar chart",
  line: "Line chart",
  table: "Table",
  pivot: "Pivot",
  scorecard: "Scorecard",
  pie: "Pie",
  funnel: "Funnel",
  text: "Text",
  control: "Control",
};

/** The kinds that produce a CHART, in the order the add menu offers them. */
export const CHART_TILE_KINDS: TileKind[] = [
  "bar",
  "line",
  "table",
  "pivot",
  "scorecard",
  "pie",
  "funnel",
];

export function isChartTile(tile: LayoutTile): tile is ChartTile {
  return "chart_uuid" in tile;
}

export function isMarkdownTile(tile: LayoutTile): tile is MarkdownTile {
  return "markdown" in tile;
}

/** The tile kind a saved chart reads back as. See `TileKind`. */
export function tileKindOf(chart: ChartSpec): TileKind {
  switch (chart.viz_type) {
    case "echarts_timeseries_bar":
      return (chart.query.groupby?.length ?? 0) > 0 ? "pivot" : "bar";
    case "echarts_timeseries_line":
      return (chart.query.groupby?.length ?? 0) > 0 ? "pivot" : "line";
    case "big_number":
    case "big_number_total":
      return "scorecard";
    case "pie":
      return "pie";
    case "funnel":
      return "funnel";
    default:
      return "table";
  }
}

function firstColumn(source: BiSource, role: "time" | "measure" | "dimension"): string | undefined {
  return (source.columns ?? []).find((c) => c.role === role)?.name;
}

/** A sensible starting metric for a source: its first measure summed, or a
 *  plain row count when the source has no measure at all. `count` with no
 *  column is `COUNT(*)`, which is legal on every source and is the one metric
 *  that can never be wrong. */
export function defaultMetric(source: BiSource): SimpleMetric {
  const measure = firstColumn(source, "measure");
  return measure
    ? { kind: "simple", aggregate: "sum", column: measure, label: measure }
    : { kind: "simple", aggregate: "count", column: null, label: "Rows" };
}

export function metricLabelFor(column: string, aggregate: SimpleAggregate): string {
  if (aggregate === "count" && !column) return "Rows";
  const verb: Record<SimpleAggregate, string> = {
    count: "Count of",
    count_distinct: "Distinct",
    sum: "Total",
    avg: "Average",
    min: "Min",
    max: "Max",
  };
  return `${verb[aggregate]} ${column}`;
}

/**
 * A fresh chart of `kind` over `source`, composed enough to preview.
 *
 * Deliberately opinionated: a new tile that draws something on its first render
 * teaches the panel by example, and a new tile that draws nothing until four
 * fields are filled in teaches nothing at all. Every default here is a legal
 * built chart under `validate_built.py` — a simple metric, no filters, no
 * extras, the source's own first time column as the axis.
 *
 * `datasetUuid` is the source's PHYSICAL dataset uuid, taken from
 * `BiSourceSummary.datasetUuid` / `BiSourceDetailResponse.datasetUuid` rather
 * than derived. `compile_studio_chart` resolves a built chart's dataset by that
 * key, so a value the client computed for itself would be an identity rule
 * living in two languages, and the symptom of the two disagreeing is every
 * chart failing to resolve a dataset that does exist. A parameter, not a
 * lookup, so there is no way to call this without having been told the answer.
 */
export function defaultChartForKind(
  kind: TileKind,
  source: BiSource,
  datasetUuid: string,
  options: { uuid?: string; title?: string } = {},
): ChartSpec {
  const uuid = options.uuid ?? newUuid();
  const metric = defaultMetric(source);
  const time = firstColumn(source, "time");
  const dimension = firstColumn(source, "dimension");
  const base = {
    uuid,
    slice_name: options.title ?? TILE_KIND_LABELS[kind],
    dataset_uuid: datasetUuid,
    source: "built" as const,
    source_id: source.id,
    description: null,
    extras: {},
  };
  const timeSpec = { time_range: "No filter", granularity_sqla: time ?? null, time_grain_sqla: time ? "P1D" : null };

  switch (kind) {
    case "bar":
    case "line":
    case "pivot": {
      const vizType = kind === "line" ? "echarts_timeseries_line" : "echarts_timeseries_bar";
      return {
        ...base,
        viz_type: vizType,
        query: {
          query_mode: "aggregate",
          metrics: [metric],
          groupby: kind === "pivot" && dimension ? [dimension] : [],
          all_columns: [],
          filters: [],
          order_by: [],
          x_axis: time ?? null,
          row_limit: 500,
          series_limit: null,
          time: timeSpec,
          extras: {},
        },
        display: {
          color_scheme: "supersetColors",
          diverging: false,
          rich_tooltip: true,
          show_labels: false,
          show_legend: true,
          show_value: false,
          x_axis_sort_asc: true,
          zoomable: false,
          annotations: null,
        },
      };
    }
    case "scorecard":
      return {
        ...base,
        viz_type: "big_number_total",
        query: {
          query_mode: "aggregate",
          metric,
          metrics: [],
          groupby: [],
          all_columns: [],
          filters: [],
          order_by: [],
          row_limit: 1,
          time: timeSpec,
          extras: {},
        },
        display: { show_trend_line: false, start_y_axis_at_zero: true },
      };
    case "pie":
      return {
        ...base,
        viz_type: "pie",
        query: {
          query_mode: "aggregate",
          metric,
          metrics: [],
          groupby: dimension ? [dimension] : [],
          all_columns: [],
          filters: [],
          order_by: [],
          row_limit: 20,
          time: { ...timeSpec, time_grain_sqla: null },
          extras: {},
        },
        display: { color_scheme: "supersetColors", donut: true, show_labels: true },
      };
    case "funnel":
      return {
        ...base,
        viz_type: "funnel",
        query: {
          query_mode: "raw",
          metrics: [],
          groupby: [],
          all_columns: (source.columns ?? []).map((c) => c.name),
          filters: [],
          order_by: [],
          row_limit: 500,
          time: { ...timeSpec, time_grain_sqla: null },
          extras: {},
        },
        display: {
          edge_count_column: "projects",
          edge_from_column: "from_stage",
          edge_to_column: "to_stage",
          reached_column: "projects_reached",
          row_kind_column: "row_kind",
          secondary_series_label: "secondary",
          stage_key_column: "stage",
          stage_label_column: "stage_label",
          stage_order_column: "stage_order",
          stalled_column: "projects_stalled",
          unit_label: "items",
        },
      };
    default:
      return {
        ...base,
        viz_type: "table",
        query: {
          query_mode: "aggregate",
          metrics: [metric],
          groupby: dimension ? [dimension] : [],
          all_columns: [],
          filters: [],
          order_by: [],
          row_limit: 100,
          time: { ...timeSpec, time_grain_sqla: null },
          extras: {},
        },
        display: {},
      };
  }
}

/**
 * Re-shape an existing chart as a different kind, keeping what still applies.
 *
 * A kind switch is the edit somebody makes most often (a bar that should have
 * been a line, a table that wanted to be a scorecard), and rebuilding from
 * scratch would throw away the measures and filters they had already chosen.
 * The fields that CANNOT survive are dropped explicitly rather than carried:
 * a scorecard takes one `metric` and no `metrics`, a raw table takes
 * `all_columns` and no aggregation, and leaving the other half populated is
 * exactly the "hybrid shape" the compiler then has to guess at.
 */
export function applyTileKind(chart: ChartSpec, kind: TileKind, source: BiSource): ChartSpec {
  // The chart's OWN `dataset_uuid` carries forward: a kind switch changes the
  // shape, never the data behind it, and re-deriving the uuid here would be the
  // client recomputing an identity the server already told it.
  const fresh = defaultChartForKind(kind, source, chart.dataset_uuid, {
    uuid: chart.uuid,
    title: chart.slice_name,
  });
  const carriedMetrics = [
    ...(chart.query.metrics ?? []),
    ...(chart.query.metric ? [chart.query.metric] : []),
  ].filter((m) => m.kind === "simple");
  const carriedFilters = (chart.query.filters ?? []).filter((f) => f.kind === "simple");
  const query = { ...fresh.query, filters: carriedFilters };
  if (carriedMetrics.length > 0) {
    if (kind === "scorecard" || kind === "pie") {
      query.metric = carriedMetrics[0] as SimpleMetric;
      query.metrics = [];
    } else if (kind !== "funnel") {
      query.metrics = carriedMetrics;
      query.metric = null;
    }
  }
  if (kind !== "funnel" && kind !== "table") {
    query.groupby = chart.query.groupby ?? fresh.query.groupby;
  }
  if (kind === "pivot" && (query.groupby?.length ?? 0) === 0) {
    query.groupby = fresh.query.groupby;
  }
  if (kind === "bar" || kind === "line") query.groupby = [];
  return { ...fresh, description: chart.description ?? null, query };
}

/** An empty dashboard, ready to have tiles dropped into it. */
export function newStudioDoc(title: string): StudioDoc {
  return {
    ir_version: "1.0.0",
    charts: {},
    spec: {
      uuid: newUuid(),
      title,
      slug: "",
      published: false,
      color_scheme: "supersetColors",
      css: null,
      layout: { rows: [], header_text: null },
      filters: [],
      parameters: [],
      cross_filters: { enabled: false },
      extras: {},
    },
  };
}

export function chartForTile(doc: StudioDoc, tile: LayoutTile): ChartSpec | undefined {
  return isChartTile(tile) ? doc.charts?.[tile.chart_uuid] : undefined;
}

export function chartAt(doc: StudioDoc, address: TileAddress): ChartSpec | undefined {
  const tile = tileAt(doc.spec.layout, address);
  return tile ? chartForTile(doc, tile) : undefined;
}

function withLayout(doc: StudioDoc, layout: StudioDoc["spec"]["layout"]): StudioDoc {
  return { ...doc, spec: { ...doc.spec, layout } };
}

/** Add a chart as a new bottom row, at half width. */
export function addChart(doc: StudioDoc, chart: ChartSpec, width = GRID_COLUMNS / 2): StudioDoc {
  const tile: ChartTile = {
    chart_uuid: chart.uuid,
    slice_name: chart.slice_name,
    width,
    height: DEFAULT_TILE_HEIGHT,
  };
  const next = withLayout(doc, appendTileRow(doc.spec.layout, tile));
  return { ...next, charts: { ...(doc.charts ?? {}), [chart.uuid]: chart } };
}

export function addMarkdown(doc: StudioDoc, markdown: string): StudioDoc {
  const tile: MarkdownTile = { markdown, width: GRID_COLUMNS, height: DEFAULT_TILE_HEIGHT };
  return withLayout(doc, appendTileRow(doc.spec.layout, tile));
}

/** Replace one chart, keeping its tile's own `slice_name` in step.
 *
 *  `ChartTile.slice_name` is a Superset fidelity field the renderer never
 *  reads, but an EXPORT writes it out, and a tile whose title disagrees with
 *  the chart it places is a puzzle for whoever reviews that pull request. */
export function putChart(doc: StudioDoc, chart: ChartSpec): StudioDoc {
  const rows = doc.spec.layout.rows.map((row) => ({
    ...row,
    tiles: row.tiles.map((tile) =>
      isChartTile(tile) && tile.chart_uuid === chart.uuid
        ? { ...tile, slice_name: chart.slice_name }
        : tile,
    ),
  }));
  return {
    ...doc,
    charts: { ...(doc.charts ?? {}), [chart.uuid]: chart },
    spec: { ...doc.spec, layout: { ...doc.spec.layout, rows } },
  };
}

export function setMarkdown(doc: StudioDoc, address: TileAddress, markdown: string): StudioDoc {
  const rows = doc.spec.layout.rows.map((row, r) => ({
    ...row,
    tiles: row.tiles.map((tile, i) =>
      r === address.row && i === address.index && isMarkdownTile(tile) ? { ...tile, markdown } : tile,
    ),
  }));
  return withLayout(doc, { ...doc.spec.layout, rows });
}

/** Remove the tile at `address`, and the chart behind it when the layout no
 *  longer places it anywhere. Charts are pruned rather than kept, because
 *  `StudioDashboard.chart_uuids` reads the LAYOUT and an unplaced chart is
 *  invisible weight in the document that a reader can never see or delete. */
export function removeTileAt(doc: StudioDoc, address: TileAddress): StudioDoc {
  const tile = tileAt(doc.spec.layout, address);
  const next = withLayout(doc, removeTile(doc.spec.layout, address));
  if (!tile || !isChartTile(tile)) return next;
  const stillPlaced = next.spec.layout.rows.some((row) =>
    row.tiles.some((t) => isChartTile(t) && t.chart_uuid === tile.chart_uuid),
  );
  if (stillPlaced) return next;
  const charts = { ...(next.charts ?? {}) };
  delete charts[tile.chart_uuid];
  return { ...next, charts };
}

/**
 * Copy the tile at `address` in beside itself.
 *
 * The highest-value edit on a per-experiment board, and the reason is the shape
 * of the work: the second tile somebody wants is almost always the first one
 * with one field changed (the same ladder for a different KPI, the same funnel
 * for a different arm source). Rebuilding it from the add menu means picking the
 * source, the measure, the breakdown and the sort again to get back to where
 * they already were.
 *
 * A duplicated CHART gets a NEW uuid, which is not a detail: the layout keys
 * tiles on `chart_uuid` and the query route fans out per chart id, so two tiles
 * sharing one id would be one chart drawn twice, and editing either would edit
 * both. The copy lands immediately after the original in the same row, so the
 * thing that just happened is on screen next to the thing it happened to.
 */
export function duplicateTileAt(doc: StudioDoc, address: TileAddress): StudioDoc {
  const tile = tileAt(doc.spec.layout, address);
  if (!tile) return doc;

  let charts = doc.charts ?? {};
  let copy: LayoutTile;
  if (isChartTile(tile)) {
    const chart = charts[tile.chart_uuid];
    if (!chart) return doc;
    const uuid = newUuid();
    const duplicated: ChartSpec = {
      ...chart,
      uuid,
      slice_name: `${chart.slice_name} (copy)`,
    };
    charts = { ...charts, [uuid]: duplicated };
    copy = { ...tile, chart_uuid: uuid, slice_name: duplicated.slice_name };
  } else {
    copy = { ...tile };
  }

  const rows = doc.spec.layout.rows.map((row, r) =>
    r === address.row
      ? { ...row, tiles: [...row.tiles.slice(0, address.index + 1), copy, ...row.tiles.slice(address.index + 1)] }
      : row,
  );
  return { ...doc, charts, spec: { ...doc.spec, layout: { ...doc.spec.layout, rows } } };
}

/** Every chart the layout actually places, in layout order. Mirrors
 *  `StudioDashboard.chart_uuids` — the same "query what is on screen" rule the
 *  backend applies, so the query route is never asked for a chart it would
 *  refuse as unknown. */
export function placedChartIds(doc: StudioDoc): string[] {
  const ids: string[] = [];
  for (const row of doc.spec.layout.rows) {
    for (const tile of row.tiles) {
      if (isChartTile(tile) && doc.charts?.[tile.chart_uuid]) ids.push(tile.chart_uuid);
    }
  }
  return ids;
}

/** Every studio source the document reads, annotations included. Mirrors
 *  `StudioDashboard.source_ids`, and the annotation half is the half that is
 *  easy to forget: an annotated series reads TWO sources, and a canvas that
 *  loads only the charts' own would draw the mark editor with no columns. */
export function sourceIdsOf(doc: StudioDoc): string[] {
  const seen = new Set<string>();
  for (const chart of Object.values(doc.charts ?? {})) {
    if (chart.source_id) seen.add(chart.source_id);
    const display = chart.display;
    if (display && "annotations" in display && display.annotations?.source_id) {
      seen.add(display.annotations.source_id);
    }
  }
  return [...seen];
}

export type ControlKind = NativeFilterSpec["kind"];

/** A new control of `kind`. `scope_chart_uuids` is left EMPTY on purpose: an
 *  empty scope means "every chart whose dataset declares this column", which is
 *  the auto-scoping `resolveFilterScope` implements and the behaviour somebody
 *  adding a filter expects. An authored list is an override, and overriding by
 *  default would make a filter stop applying to a chart added after it. */
export function newControl(kind: ControlKind, options: Partial<NativeFilterSpec> = {}): NativeFilterSpec {
  return {
    id: newUuid(),
    kind,
    name: kind === "time_range" ? "Date range" : kind === "range" ? "Range" : "Filter",
    options: [],
    options_from: null,
    options_result_column: null,
    default_value: null,
    enable_empty_filter: false,
    multi_select: true,
    scope_chart_uuids: [],
    target_column: null,
    target_dataset_uuid: null,
    target_parameter_high: null,
    target_parameter_low: null,
    ...options,
  };
}

export function addControl(doc: StudioDoc, control: NativeFilterSpec): StudioDoc {
  return { ...doc, spec: { ...doc.spec, filters: [...doc.spec.filters, control] } };
}

export function putControl(doc: StudioDoc, control: NativeFilterSpec): StudioDoc {
  return {
    ...doc,
    spec: {
      ...doc.spec,
      filters: doc.spec.filters.map((f) => (f.id === control.id ? control : f)),
    },
  };
}

/** Remove a control, and any dashboard PARAMETER that pointed at it.
 *
 *  A parameter naming a filter that no longer exists is a template whose URL
 *  value silently does nothing — the failure mode that makes `/x/<key>` look
 *  like it works while showing every experiment at once. */
export function removeControl(doc: StudioDoc, controlId: string): StudioDoc {
  return {
    ...doc,
    spec: {
      ...doc.spec,
      filters: doc.spec.filters.filter((f) => f.id !== controlId),
      parameters: (doc.spec.parameters ?? []).filter((p) => p.filter_id !== controlId),
    },
  };
}

export function putParameter(doc: StudioDoc, parameter: DashboardParameter): StudioDoc {
  const existing = (doc.spec.parameters ?? []).filter((p) => p.name !== parameter.name);
  return { ...doc, spec: { ...doc.spec, parameters: [...existing, parameter] } };
}

export function removeParameter(doc: StudioDoc, name: string): StudioDoc {
  return {
    ...doc,
    spec: {
      ...doc.spec,
      parameters: (doc.spec.parameters ?? []).filter((p) => p.name !== name),
    },
  };
}

export function setTitle(doc: StudioDoc, title: string): StudioDoc {
  return { ...doc, spec: { ...doc.spec, title } };
}
